import React, { useState, useEffect } from 'react';
import { Filter, Grid3X3, List, SlidersHorizontal, Shield, Radio, DoorOpen, Flame, Droplets, Keyboard, Volume2, Box, Package } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import ShopCategoryNav from '../components/ShopCategoryNav';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

const ShopPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('name');
  const [categoryFilter, setCategoryFilter] = useState('all'); // Main category filter
  const [filters, setFilters] = useState({
    kits: false,
    hubs: false,
    motion: false,
    door: false,
    fire: false,
    water: false,
    keypads: false,
    sirens: false,
    accessories: false,
  });

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${API}/products?category=basic`);
        if (response.ok) {
          const data = await response.json();
          setProducts(data);
        }
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const getProductType = (product) => {
    const name = product.name.toLowerCase();
    if (name.includes('starterkit') || name.includes('kit')) return 'kits';
    if (name.includes('hub')) return 'hubs';
    if (name.includes('motionprotect') || name.includes('motion')) return 'motion';
    if (name.includes('doorprotect') || name.includes('door')) return 'door';
    if (name.includes('fireprotect') || name.includes('fire')) return 'fire';
    if (name.includes('leaksprotect') || name.includes('leak') || name.includes('waterstop')) return 'water';
    if (name.includes('keypad')) return 'keypads';
    if (name.includes('siren') || name.includes('homesiren') || name.includes('streetsiren')) return 'sirens';
    return 'accessories';
  };

  // Category definitions with icons
  const categoryDefinitions = [
    { key: 'all', label: 'Tous', icon: Package, color: 'purple' },
    { key: 'kits', label: 'Kits', icon: Box, color: 'green' },
    { key: 'hubs', label: 'Centrales', icon: Radio, color: 'blue' },
    { key: 'motion', label: 'Mouvement', icon: Shield, color: 'purple' },
    { key: 'door', label: 'Ouverture', icon: DoorOpen, color: 'cyan' },
    { key: 'fire', label: 'Incendie', icon: Flame, color: 'red' },
    { key: 'water', label: 'Inondation', icon: Droplets, color: 'blue' },
    { key: 'keypads', label: 'Claviers', icon: Keyboard, color: 'gray' },
    { key: 'sirens', label: 'Sirènes', icon: Volume2, color: 'orange' },
  ];

  // Count products by category
  const getCategoryCount = (categoryKey) => {
    if (categoryKey === 'all') return products.length;
    return products.filter(p => getProductType(p) === categoryKey).length;
  };

  const filteredProducts = products.filter(product => {
    const type = getProductType(product);
    // If a specific category is selected via header buttons, only show that category
    if (categoryFilter !== 'all') {
      return type === categoryFilter;
    }
    // Check if any checkbox filter is active
    const hasActiveFilters = Object.values(filters).some(v => v === true);
    if (!hasActiveFilters) {
      // No filters checked = show all products
      return true;
    }
    // Some filters are checked = show only checked categories
    return filters[type] === true;
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    // Special priority: Hub 2 (4G) first
    if (a.name.includes('Hub 2 (4G)') && !b.name.includes('Hub 2 (4G)')) return -1;
    if (!a.name.includes('Hub 2 (4G)') && b.name.includes('Hub 2 (4G)')) return 1;
    
    switch (sortBy) {
      case 'price-asc':
        return (a.price || 0) - (b.price || 0);
      case 'price-desc':
        return (b.price || 0) - (a.price || 0);
      case 'name':
      default:
        return a.name.localeCompare(b.name);
    }
  });

  const filterCategories = [
    { key: 'kits', label: 'Kits de démarrage' },
    { key: 'hubs', label: 'Centrales' },
    { key: 'motion', label: 'Détecteurs de mouvement' },
    { key: 'door', label: 'Détecteurs d\'ouverture' },
    { key: 'fire', label: 'Détecteurs incendie' },
    { key: 'water', label: 'Détecteurs inondation' },
    { key: 'keypads', label: 'Claviers' },
    { key: 'sirens', label: 'Sirènes' },
    { key: 'accessories', label: 'Accessoires' },
  ];

  const FilterContent = () => (
    <div className="space-y-6">
      <div>
        <h4 className="text-xs font-medium text-white/50 mb-4 uppercase tracking-wider">Catégories</h4>
        <div className="space-y-3">
          {filterCategories.map(({ key, label }) => (
            <div key={key} className="flex items-center space-x-3">
              <Checkbox
                id={key}
                checked={filters[key]}
                onCheckedChange={(checked) => setFilters(prev => ({ ...prev, [key]: checked }))}
                className="border-white/20 data-[state=checked]:bg-white data-[state=checked]:border-white data-[state=checked]:text-black"
                data-testid={`filter-${key}`}
              />
              <Label 
                htmlFor={key} 
                className="text-white/50 hover:text-white cursor-pointer transition-colors text-sm"
              >
                {label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      <div className="pt-4 border-t border-white/5">
        <Button 
          variant="outline" 
          className="w-full border-white/10 text-white/50 hover:text-white hover:bg-white/5 rounded-full"
          onClick={() => {
            setCategoryFilter('all');
            setFilters({
              kits: false,
              hubs: false,
              motion: false,
              door: false,
              fire: false,
              water: false,
              keypads: false,
              sirens: false,
              accessories: false,
            });
          }}
        >
          Réinitialiser
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen py-12 bg-black">
      <div className="pt-2">
        <ShopCategoryNav />
      </div>
      {/* Header */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-12 mb-12">
        <div className="relative py-16 rounded-2xl md:rounded-3xl overflow-hidden bg-[#0a0a0a] border border-white/5">
          <div className="relative text-center">
            <h1 className="font-heading text-3xl md:text-5xl font-semibold text-white mb-4 tracking-tight">
              Baseline Alarme Sans Fil
            </h1>
            <p className="text-white/60 text-base md:text-lg max-w-2xl mx-auto mb-8 font-light">
              Gamme Ajax Jeweller — Jusqu'à -20%
            </p>
            
            {/* Category Filter Buttons */}
            <div className="flex flex-wrap justify-center gap-2 md:gap-3 px-4">
              {categoryDefinitions.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setCategoryFilter(key)}
                  data-testid={`category-${key}`}
                  className={`px-3 md:px-4 py-2 rounded-full font-medium transition-all flex items-center gap-2 text-sm ${
                    categoryFilter === key
                      ? 'bg-white text-black'
                      : 'bg-white/5 border border-white/10 text-white/60 hover:text-white hover:border-white/20'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{label}</span>
                  <span className="sm:hidden">{label.split(' ')[0]}</span>
                  {key !== 'all' && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                      categoryFilter === key ? 'bg-black/10' : 'bg-white/10'
                    }`}>
                      {getCategoryCount(key)}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Exclusivité Garantie 4 ans */}
      <section className="max-w-[1400px] mx-auto px-4 md:px-12 mb-8">
        <div className="bg-emerald-500/5 border border-fgs-primary/20 rounded-xl p-5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="flex-1">
              <p className="text-white/70">
                <span className="font-semibold text-emerald-400">Exclusivité FGS</span> — Nous garantissons <strong className="text-white">tous les produits Ajax pendant 4 ans</strong>, 
                soit 2 ans de plus que la garantie constructeur standard.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-4 md:px-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar - Desktop */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-20 bg-[#0a0a0a] border border-white/5 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-6">
                <Filter className="w-5 h-5 text-white/50" />
                <span className="text-sm font-medium text-white uppercase tracking-wider">Filtres</span>
              </div>
              <FilterContent />
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <p className="text-white/60 text-sm">
                {sortedProducts.length} produit{sortedProducts.length > 1 ? 's' : ''}
              </p>

              <div className="flex items-center gap-3">
                {/* Mobile Filter */}
                <Sheet>
                  <SheetTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="lg:hidden border-white/10 text-white/60 hover:bg-white/5"
                      data-testid="mobile-filter-btn"
                    >
                      <SlidersHorizontal className="w-4 h-4 mr-2" />
                      Filtres
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="bg-[#0a0a0a] border-white/5">
                    <SheetHeader>
                      <SheetTitle className="text-white">Filtres</SheetTitle>
                    </SheetHeader>
                    <div className="mt-6">
                      <FilterContent />
                    </div>
                  </SheetContent>
                </Sheet>

                {/* Sort */}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="bg-[#0a0a0a] border border-white/10 rounded-lg px-4 py-2 text-sm text-white/70 focus:border-white/20 outline-none"
                  data-testid="sort-select"
                >
                  <option value="name">Nom A-Z</option>
                  <option value="price-asc">Prix croissant</option>
                  <option value="price-desc">Prix décroissant</option>
                </select>

                {/* View Mode */}
                <div className="hidden sm:flex items-center gap-1 bg-[#0a0a0a] border border-white/10 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-md ${viewMode === 'grid' ? 'bg-white text-black' : 'text-white/60 hover:text-white'}`}
                    data-testid="view-grid"
                  >
                    <Grid3X3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-md ${viewMode === 'list' ? 'bg-white text-black' : 'text-white/60 hover:text-white'}`}
                    data-testid="view-list"
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Products Grid */}
            {loading ? (
              <div className={`grid gap-4 md:gap-6 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1'}`}>
                {[...Array(9)].map((_, i) => (
                  <div key={i} className="bg-white/5 rounded-2xl h-96 animate-pulse" />
                ))}
              </div>
            ) : sortedProducts.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-white/60 text-lg">Aucun produit ne correspond à vos critères.</p>
                <Button 
                  variant="outline" 
                  className="mt-4 border-white/20 text-white hover:bg-white/5 rounded-full"
                  onClick={() => {
                    setCategoryFilter('all');
                    setFilters({
                      kits: false,
                      hubs: false,
                      motion: false,
                      door: false,
                      fire: false,
                      water: false,
                      keypads: false,
                      sirens: false,
                      accessories: false,
                    });
                  }}
                >
                  Voir tous les produits
                </Button>
              </div>
            ) : (
              <div className={`grid gap-4 md:gap-6 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1'}`}>
                {sortedProducts.map((product) => (
                  <ProductCard key={product.product_id} product={product} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopPage;
